import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/file-upload-modal.css";

const CloseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const UploadIcon = () => (
    <svg className="fum-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

const FileIcon = () => (
    <svg className="fum-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

function uploadWithProgress(
    url: string,
    file: File,
    fieldName: string,
    onProgress: (pct: number) => void,
): Promise<unknown> {
    const formData = new FormData();
    formData.append(fieldName, file);

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                onProgress(Math.round((e.loaded / e.total) * 100));
            }
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch {
                    resolve({});
                }
            } else if (xhr.status === 413) {
                reject(new Error("File is too large. Please choose a smaller file."));
            } else {
                reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(formData);
    });
}

export type FileUploadModalProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    accept: string;
    maxSizeBytes: number;
    hint: string;
    previewMode?: "image" | "file";

    /** Custom validation; return error message or null if valid */
    validateFile?: (file: File) => string | null;
    onValidationError?: (message: string) => void;
    onFileSelect?: (file: File) => void;

    mode?: "deferred" | "immediate" | "custom";
    onSelected?: (file: File) => void;
    onSubmit?: (file: File) => void | Promise<void>;
    uploadUrl?: string;
    uploadFieldName?: string;
    onUploaded?: (url?: string) => void;
    /** Raw JSON body from a successful immediate upload (e.g. media kit photo metadata). */
    onUploadResponse?: (json: Record<string, unknown>) => void;

    submitLabel?: string;
    submitDisabled?: boolean;
    busy?: boolean;
    progress?: number;
    progressText?: string;

    children?: React.ReactNode;
};

export default function FileUploadModal({
    open,
    onClose,
    title,
    accept,
    maxSizeBytes,
    hint,
    previewMode = "file",
    validateFile,
    onValidationError,
    onFileSelect,
    mode = "deferred",
    onSelected,
    onSubmit,
    uploadUrl,
    uploadFieldName = "file",
    onUploaded,
    onUploadResponse,
    submitLabel,
    submitDisabled = false,
    busy = false,
    progress,
    progressText,
    children,
}: FileUploadModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState("");
    const [internalBusy, setInternalBusy] = useState(false);
    const [internalProgress, setInternalProgress] = useState(0);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const inputId = useId();

    const isBusy = busy || internalBusy;
    const displayProgress = progress ?? internalProgress;

    useEffect(() => {
        if (!file || previewMode !== "image") {
            setImagePreview(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setImagePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file, previewMode]);

    useEffect(() => {
        if (!open) {
            setFile(null);
            setDragOver(false);
            setError("");
            setInternalBusy(false);
            setInternalProgress(0);
        }
    }, [open]);

    const showError = useCallback(
        (message: string) => {
            setError(message);
            onValidationError?.(message);
        },
        [onValidationError],
    );

    const handleFilePick = useCallback(
        (selectedFile: File | null | undefined) => {
            if (!selectedFile || isBusy) return;

            setError("");

            if (selectedFile.size > maxSizeBytes) {
                const maxMb = (maxSizeBytes / (1024 * 1024)).toFixed(0);
                showError(`File must be ${maxMb}MB or less. Please choose a smaller file.`);
                return;
            }

            if (validateFile) {
                const validationError = validateFile(selectedFile);
                if (validationError) {
                    showError(validationError);
                    return;
                }
            }

            setFile(selectedFile);
            onFileSelect?.(selectedFile);
        },
        [isBusy, maxSizeBytes, onFileSelect, showError, validateFile],
    );

    const clearFile = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isBusy) return;
            setFile(null);
            setError("");
            if (inputRef.current) {
                inputRef.current.value = "";
            }
        },
        [isBusy],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLElement>) => {
            e.preventDefault();
            setDragOver(false);
            handleFilePick(e.dataTransfer.files[0]);
        },
        [handleFilePick],
    );

    const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        if (!isBusy) setDragOver(true);
    }, [isBusy]);

    const handleClose = useCallback(() => {
        if (!isBusy) onClose();
    }, [isBusy, onClose]);

    const handlePrimaryAction = async () => {
        if (!file) {
            setError("Please choose a file.");
            return;
        }

        if (mode === "deferred") {
            onSelected?.(file);
            onClose();
            return;
        }

        if (mode === "custom") {
            try {
                await onSubmit?.(file);
            } catch (err) {
                setError((err as Error).message || "Upload failed.");
            }
            return;
        }

        if (!uploadUrl) {
            setError("No upload target configured.");
            return;
        }

        setInternalBusy(true);
        setInternalProgress(0);
        setError("");

        try {
            const json = (await uploadWithProgress(
                uploadUrl,
                file,
                uploadFieldName,
                setInternalProgress,
            )) as Record<string, unknown>;

            const url =
                (json?.imageUrl as string | undefined) ||
                (json?.url as string | undefined) ||
                (json?.profileImageUrl as string | undefined) ||
                (json?.companyLogoUrl as string | undefined) ||
                (json?.location as string | undefined);

            onUploadResponse?.(json);
            onUploaded?.(url);
            onClose();
        } catch (err) {
            setError((err as Error).message || "Upload failed.");
        } finally {
            setInternalBusy(false);
        }
    };

    if (!open) return null;

    const defaultSubmitLabel =
        mode === "deferred" ? "Use this file" : mode === "immediate" ? "Upload" : "Submit";

    const primaryLabel = submitLabel ?? (isBusy ? "Uploading..." : defaultSubmitLabel);
    const primaryDisabled = !file || submitDisabled || isBusy;
    const dropzoneClassName = `fum-dropzone ${dragOver ? "fum-drag-over" : ""} ${file ? "fum-has-file" : ""}`;
    const dropzoneDragProps = {
        onDragOver: handleDragOver,
        onDragLeave: () => setDragOver(false),
        onDrop: handleDrop,
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    };

    return createPortal(
        <>
            <div className="fum-overlay" onClick={handleClose} aria-hidden="true" />
            <div className="fum-modal" role="dialog" aria-modal="true" aria-label={title}>
                <div className="fum-header">
                    <h2>{title}</h2>
                    <button
                        type="button"
                        className="fum-icon-btn"
                        onClick={handleClose}
                        disabled={isBusy}
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div className="fum-body">
                    {file ? (
                        <div className={dropzoneClassName} {...dropzoneDragProps}>
                            {previewMode === "image" && imagePreview ? (
                                <div className="fum-image-preview-row">
                                    <img
                                        src={imagePreview}
                                        alt="Selected preview"
                                        className="fum-image-preview"
                                    />
                                    <div className="fum-file-info">
                                        <span className="fum-file-name">{file.name}</span>
                                        <span className="fum-file-size">{formatFileSize(file.size)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="fum-icon-btn"
                                        onClick={clearFile}
                                        disabled={isBusy}
                                        aria-label="Remove file"
                                    >
                                        <CloseIcon />
                                    </button>
                                </div>
                            ) : (
                                <div className="fum-file-preview">
                                    <FileIcon />
                                    <div className="fum-file-info">
                                        <span className="fum-file-name">{file.name}</span>
                                        <span className="fum-file-size">{formatFileSize(file.size)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="fum-icon-btn"
                                        onClick={clearFile}
                                        disabled={isBusy}
                                        aria-label="Remove file"
                                    >
                                        <CloseIcon />
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <label
                            htmlFor={inputId}
                            className={dropzoneClassName}
                            {...dropzoneDragProps}
                        >
                            <UploadIcon />
                            <p>
                                <strong>Click to upload</strong> or drag and drop
                            </p>
                            <p className="fum-dropzone-hint">{hint}</p>
                        </label>
                    )}
                    {/* Sibling of the dropzone, visually hidden (not display:none). A nested
                        input.click() from a div onClick is suppressed by the browser. */}
                    <input
                        id={inputId}
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        className="fum-file-input"
                        aria-label={title}
                        disabled={isBusy}
                        onChange={(e) => handleFilePick(e.target.files?.[0])}
                    />

                    {children}

                    {error && <div className="fum-error">{error}</div>}

                    {isBusy && (
                        <div className="fum-progress-section">
                            <div className="fum-progress-bar">
                                <div
                                    className="fum-progress-fill"
                                    style={{ width: `${displayProgress}%` }}
                                />
                            </div>
                            <p className="fum-progress-text">
                                {progressText ??
                                    (displayProgress < 100
                                        ? `Uploading... ${displayProgress}%`
                                        : "Processing...")}
                            </p>
                        </div>
                    )}
                </div>

                <div className="fum-footer">
                    <button
                        type="button"
                        className="fum-btn-secondary"
                        onClick={handleClose}
                        disabled={isBusy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="fum-btn-primary"
                        onClick={() => void handlePrimaryAction()}
                        disabled={primaryDisabled}
                    >
                        {primaryLabel}
                    </button>
                </div>
            </div>
        </>,
        document.body,
    );
}
